import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import TranslationProvider from '../../providers/TranslationProvider';
import { SignupForm } from '../Form';
import containerQueryProps from '../../props/containerQueryProps';

import './Auth.scss';
import { AuthAnalyticsEvents } from '../../containers/AuthContainer/authSteps';
import {
  AUTH_ACTIONS,
  AUTH_FLOWS,
  AUTH_STEPS,
  buildAuthDdActionName,
  normalizeAuthMethod,
} from '../../utils/authDatadog';

const SignupView = ({
  AutomationMode,
  countryCodes,
  defaultCountryCode,
  grecaptcha,
  errorMessage,
  onSubmit,
  containerQuery,
  venue,
  phone,
  cta,
  passwordRequired,
  firstName,
  lastName,
  email,
  mobile,
  marketingOptInChecked,
  isNonUS,
  event,
  method,
}) => {
  const { sm } = containerQuery;

  useEffect(() => {
    event(AuthAnalyticsEvents.START_SIGN_UP, {
      method,
    });
  }, []);

  return (
    <div
      className={cx('AuthView', {
        'AuthView--sm': sm,
      })}
      style={{ paddingBottom: '75px' }}
    >
      <TranslationProvider
        translationKey="onboarding.signup_title"
      >
        <h2 className="AuthView__Headline" style={{ marginBottom: '10px' }}>
          Please create an account to continue.
        </h2>
      </TranslationProvider>
      <TranslationProvider
        translationKey={venue ? 'onboarding.signup_copy' : 'onboarding.signup_copy_secondary'}
        translationValues={{
          venueName: venue,
        }}
      >
        <div className="AuthView__Text--light" style={{ marginBottom: '5px' }}>
          Let’s get some basic information so that restaurants know who you are.
        </div>
      </TranslationProvider>
      <TranslationProvider translationKey="required_field">
        <div className="required_field_label">
          Required fields marked with an asterisk (*).
        </div>
      </TranslationProvider>

      <SignupForm
        AutomationMode={AutomationMode}
        fullBleedBtn
        onSubmit={onSubmit}
        grecaptcha={grecaptcha}
        errorMessage={errorMessage}
        containerQuery={containerQuery}
        venue={venue}
        phone={phone}
        cta={cta}
        passwordRequired={passwordRequired}
        firstName={firstName}
        lastName={lastName}
        email={email}
        mobile={mobile}
        countryCodes={countryCodes}
        defaultCountryCode={defaultCountryCode}
        marketingOptInChecked={marketingOptInChecked}
        isNonUS={isNonUS}
        datadogActionName={buildAuthDdActionName({
          flow: AUTH_FLOWS.SIGN_UP,
          method: normalizeAuthMethod(method),
          step: AUTH_STEPS.CAPTURE_SIGNUP_DETAILS,
          action: AUTH_ACTIONS.SUBMIT,
        })}
      />
    </div>
  );
};

SignupView.propTypes = {
  AutomationMode: PropTypes.bool.isRequired,
  countryCodes: PropTypes.arrayOf(PropTypes.shape({
    iso3166: PropTypes.string,
    code: PropTypes.string,
    country_name: PropTypes.string,
  })),
  defaultCountryCode: PropTypes.string,
  grecaptcha: PropTypes.shape({
    render: PropTypes.func.isRequired,
  }).isRequired,
  errorMessage: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  venue: PropTypes.string,
  containerQuery: containerQueryProps,
  passwordRequired: PropTypes.bool,
  cta: PropTypes.string,
  phone: PropTypes.string,
  event: PropTypes.func,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  mobile: PropTypes.string,
  marketingOptInChecked: PropTypes.bool,
  isNonUS: PropTypes.bool,
  method: PropTypes.string.isRequired,
};

SignupView.defaultProps = {
  countryCodes: undefined,
  defaultCountryCode: '1',
  errorMessage: undefined,
  venue: null,
  containerQuery: {},
  passwordRequired: true,
  cta: 'Create Account',
  phone: '',
  event: () => {},
  firstName: '',
  lastName: '',
  email: '',
  mobile: '',
  marketingOptInChecked: false,
  isNonUS: undefined,
};

SignupView.displayName = 'SignupView';

export default SignupView;
